import React, { useCallback, useEffect, useState } from "react";
import {
  Box,
  Button,
  FormControl,
  FormLabel,
  HStack,
  Input,
  Modal,
  ModalBody,
  ModalCloseButton,
  ModalContent,
  ModalFooter,
  ModalHeader,
  ModalOverlay,
  Select,
  SimpleGrid,
  Td,
  Textarea,
  Tr,
  useDisclosure,
  useToast,
} from "@chakra-ui/react";
import { AddIcon, DeleteIcon, EditIcon } from "@chakra-ui/icons";
import { FaTasks } from "react-icons/fa";
import { useTranslation } from "react-i18next";
import { useAuth } from "../../../context/AuthContext";
import { useBusinessUnit } from "../../../context/BusinessUnitContext";
import {
  createTask,
  deleteTask,
  getMyTasks,
  listTasks,
  updateTask,
} from "../../../api/tasksApi";
import { listMentionableUsers } from "../../../api/authApi";
import { listConsultants } from "../../../api/consultantsApi";
import { listClients } from "../../../api/clientsApi";
import { listNeeds } from "../../../api/needsApi";
import { listMatches } from "../../../api/matchesApi";
import { isCompanyAdmin, isManagerRole } from "../../../constants/roles";
import ConfirmDialog from "../../../components/Portal/ConfirmDialog";
import {
  MODAL_CONTENT_SX,
  MODAL_HEADER_SX,
  MODAL_FOOTER_SX,
} from "../../../components/Portal/portalModalChrome";
import {
  PageHeader,
  KpiStrip,
  KpiCard,
  Panel,
  DataTable,
  StatusBadge,
  MiniIconButton,
  FilterBar,
  EmptyState,
  TablePagination,
} from "../../../components/Portal/ui";
import {
  TASK_STATUS_COLORS,
  TASK_PRIORITY_COLORS,
} from "../../../constants/statuses";

const PAGE_SIZE = 50;

const STATUS_OPTIONS = ["open", "in_progress", "done", "cancelled"];
const PRIORITY_OPTIONS = ["low", "normal", "high"];
const ENTITY_TYPE_OPTIONS = ["consultant", "client", "need", "match"];

// Compact 31px filter-row select, matching FilterSearch (per migrated analogs).
const FILTER_SELECT_SX = {
  height: "31px !important",
  paddingTop: "0 !important",
  paddingBottom: "0 !important",
  fontWeight: 600,
};

const emptyForm = {
  title: "",
  description: "",
  due_date: "",
  status: "open",
  priority: "normal",
  entity_type: "",
  entity_id: "",
  assigned_to_user_id: "",
};

export default function Tasks() {
  const { t } = useTranslation("portal");
  const { user } = useAuth();
  const { selectedBusinessUnitId } = useBusinessUnit();
  const buId = selectedBusinessUnitId || undefined;
  const toast = useToast();

  const [tasks, setTasks] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [buUsers, setBuUsers] = useState([]);
  const [entityOptions, setEntityOptions] = useState([]);
  const [entityLoading, setEntityLoading] = useState(false);
  const [page, setPage] = useState(1);
  const [filterStatus, setFilterStatus] = useState("");
  const [filterPriority, setFilterPriority] = useState("");
  const [viewAll, setViewAll] = useState(false);

  const role = user?.role || "";
  const canViewAll = isCompanyAdmin(role) || isManagerRole(role);

  // Form modal
  const { isOpen, onOpen, onClose } = useDisclosure();
  const [form, setForm] = useState(emptyForm);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);

  // Delete dialog
  const {
    isOpen: isDeleteOpen,
    onOpen: onDeleteOpen,
    onClose: onDeleteClose,
  } = useDisclosure();
  const [deletingId, setDeletingId] = useState(null);

  const fetchTasks = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, page_size: PAGE_SIZE };
      if (filterStatus) params.status = filterStatus;
      if (filterPriority) params.priority = filterPriority;
      const opts = { buId };
      const fn = viewAll && canViewAll ? listTasks : getMyTasks;
      const data = await fn(params, opts);
      setTasks(data.items || []);
      setTotal(data.total || 0);
    } catch (err) {
      toast({
        title: t("tasks.loadError", "Failed to load tasks"),
        status: "error",
        duration: 3000,
      });
    } finally {
      setLoading(false);
    }
  }, [buId, page, filterStatus, filterPriority, viewAll, canViewAll, t, toast]);

  useEffect(() => {
    fetchTasks();
  }, [fetchTasks]);

  useEffect(() => {
    let cancelled = false;
    const fetchUsers = async () => {
      try {
        const data = await listMentionableUsers({ buId });
        if (!cancelled) setBuUsers(Array.isArray(data) ? data : []);
      } catch {
        if (!cancelled) setBuUsers([]);
      }
    };
    fetchUsers();
    return () => {
      cancelled = true;
    };
  }, [buId]);

  // Fetch entity options when entity_type changes
  useEffect(() => {
    let cancelled = false;
    if (!form.entity_type) {
      setEntityOptions([]);
      return;
    }
    const fetchEntities = async () => {
      setEntityLoading(true);
      try {
        let items = [];
        const params = { page: 1, pageSize: 500 };
        if (form.entity_type === "consultant") {
          const data = await listConsultants(params, { buId });
          items = (data.items || []).map((c) => ({ id: c.id, label: c.name || `#${c.id}` }));
        } else if (form.entity_type === "client") {
          const data = await listClients(params, { buId });
          items = (data.items || []).map((c) => ({ id: c.id, label: c.name || `#${c.id}` }));
        } else if (form.entity_type === "need") {
          const data = await listNeeds(params, { buId });
          items = (data.items || []).map((n) => ({
            id: n.id,
            label: `${n.title || "Untitled"}${n.client_name || n.clientName ? ` — ${n.client_name || n.clientName}` : ""}`,
          }));
        } else if (form.entity_type === "match") {
          const data = await listMatches(params, { buId });
          items = (data.items || []).map((m) => ({
            id: m.id,
            label: `${m.consultant_name || m.consultantName || "Consultant"} → ${m.need_title || m.needTitle || `Match #${m.id}`}`,
          }));
        }
        if (!cancelled) setEntityOptions(items);
      } catch {
        if (!cancelled) setEntityOptions([]);
      } finally {
        if (!cancelled) setEntityLoading(false);
      }
    };
    fetchEntities();
    return () => {
      cancelled = true;
    };
  }, [form.entity_type, buId]);

  const handleOpenCreate = () => {
    setForm(emptyForm);
    setEditingId(null);
    onOpen();
  };

  const handleOpenEdit = (task) => {
    setForm({
      title: task.title || "",
      description: task.description || "",
      due_date: task.due_date || "",
      status: task.status || "open",
      priority: task.priority || "normal",
      entity_type: task.entity_type || "",
      entity_id: task.entity_id || "",
      assigned_to_user_id: task.assigned_to_user_id || "",
    });
    setEditingId(task.id);
    onOpen();
  };

  const handleSave = async () => {
    if (!form.title.trim()) {
      toast({
        title: t("tasks.titleRequired", "Title is required"),
        status: "warning",
        duration: 2000,
      });
      return;
    }
    setSaving(true);
    try {
      const payload = {
        ...form,
        entity_id: form.entity_id ? Number(form.entity_id) : null,
        entity_type: form.entity_type || null,
        assigned_to_user_id: form.assigned_to_user_id
          ? Number(form.assigned_to_user_id)
          : null,
        due_date: form.due_date || null,
      };
      if (editingId) {
        await updateTask(editingId, payload, { buId });
        toast({
          title: t("tasks.updated", "Task updated"),
          status: "success",
          duration: 2000,
        });
      } else {
        await createTask(payload, { buId });
        toast({
          title: t("tasks.created", "Task created"),
          status: "success",
          duration: 2000,
        });
      }
      onClose();
      fetchTasks();
    } catch (err) {
      toast({
        title: t("tasks.saveError", "Failed to save task"),
        status: "error",
        duration: 3000,
      });
    } finally {
      setSaving(false);
    }
  };

  const handleDelete = async () => {
    if (!deletingId) return;
    try {
      await deleteTask(deletingId, { buId });
      toast({
        title: t("tasks.deleted", "Task deleted"),
        status: "success",
        duration: 2000,
      });
      onDeleteClose();
      fetchTasks();
    } catch (err) {
      toast({
        title: t("tasks.deleteError", "Failed to delete task"),
        status: "error",
        duration: 3000,
      });
    }
  };

  const openCount = tasks.filter((task) => task.status === "open").length;
  const inProgressCount = tasks.filter(
    (task) => task.status === "in_progress"
  ).length;
  const doneCount = tasks.filter((task) => task.status === "done").length;

  const totalPages = Math.max(1, Math.ceil(total / PAGE_SIZE));

  const columns = [
    { key: "title", label: t("tasks.titleCol", "Title"), width: "22%" },
    { key: "status", label: t("tasks.statusCol", "Status"), width: "11%" },
    { key: "priority", label: t("tasks.priorityCol", "Priority"), width: "10%" },
    { key: "due", label: t("tasks.dueDateCol", "Due date"), width: "11%" },
    { key: "owner", label: t("tasks.ownerCol", "Owner"), width: "13%" },
    { key: "assignee", label: t("tasks.assigneeCol", "Assignee"), width: "13%" },
    { key: "entity", label: t("tasks.entityCol", "Linked to") },
    {
      key: "actions",
      label: t("tasks.actionsCol", "Actions"),
      width: "10%",
      align: "right",
    },
  ];

  return (
    // Bleed past PortalLayout's p={6} so the page-head spans the content edges.
    <Box mt="-24px" mx="-24px" mb="-24px" minH="100%">
      <PageHeader
        moduleCode="PT"
        eyebrow={t("tasks.eyebrow", "ASAP Pilot")}
        title={t("tasks.pageTitle", "Tasks")}
        actions={
          <>
            {canViewAll && (
              <Button
                data-testid="tasks-viewall-btn"
                variant={viewAll ? "primary" : "secondary"}
                size="sm"
                onClick={() => {
                  setViewAll((v) => !v);
                  setPage(1);
                }}
              >
                {viewAll
                  ? t("tasks.viewMine", "My tasks")
                  : t("tasks.viewAll", "All tasks")}
              </Button>
            )}
            <Button
              data-testid="tasks-create-btn"
              variant="primary"
              size="sm"
              leftIcon={<AddIcon boxSize="9px" />}
              onClick={handleOpenCreate}
            >
              {t("tasks.create", "New task")}
            </Button>
          </>
        }
      />

      <Box p="20px">
        <KpiStrip mb="12px">
          <KpiCard label={t("tasks.totalTasks", "Total")} value={total} />
          <KpiCard label={t("tasks.openTasks", "Open")} value={openCount} />
          <KpiCard
            label={t("tasks.inProgressTasks", "In progress")}
            value={inProgressCount}
          />
          <KpiCard label={t("tasks.doneTasks", "Done")} value={doneCount} />
        </KpiStrip>

        <Panel
          title={t("tasks.pageTitle", "Tasks")}
          count={
            loading
              ? t("tasks.summary.loading", "Loading...")
              : t("tasks.summary.total", {
                  count: total,
                  defaultValue: "{{count}} task(s)",
                })
          }
          filters={
            <FilterBar>
              <Select
                size="sm"
                w={{ base: "100%", sm: "180px" }}
                flex="0 0 auto"
                placeholder={t("tasks.allStatuses", "All statuses")}
                value={filterStatus}
                onChange={(e) => {
                  setFilterStatus(e.target.value);
                  setPage(1);
                }}
                sx={FILTER_SELECT_SX}
              >
                {STATUS_OPTIONS.map((s) => (
                  <option key={s} value={s}>
                    {t(`tasks.status.${s}`, s)}
                  </option>
                ))}
              </Select>
              <Select
                size="sm"
                w={{ base: "100%", sm: "180px" }}
                flex="0 0 auto"
                placeholder={t("tasks.allPriorities", "All priorities")}
                value={filterPriority}
                onChange={(e) => {
                  setFilterPriority(e.target.value);
                  setPage(1);
                }}
                sx={FILTER_SELECT_SX}
              >
                {PRIORITY_OPTIONS.map((p) => (
                  <option key={p} value={p}>
                    {t(`tasks.priority.${p}`, p)}
                  </option>
                ))}
              </Select>
            </FilterBar>
          }
          footer={
            <TablePagination
              page={page}
              pageSize={PAGE_SIZE}
              totalItems={total}
              totalPages={totalPages}
              onPageChange={setPage}
              onPageSizeChange={() => {}}
            />
          }
        >
          <DataTable
            columns={columns}
            rows={tasks}
            loading={loading}
            emptyState={
              <EmptyTasks t={t} />
            }
            renderRow={(task) => (
              <Tr key={task.id} data-testid={`task-row-${task.id}`}>
                <Td fontWeight="700" title={task.title || ""}>
                  {task.title}
                </Td>
                <Td>
                  <StatusBadge
                    label={t(`tasks.status.${task.status}`, task.status)}
                    colorScheme={TASK_STATUS_COLORS[task.status] || "gray"}
                  />
                </Td>
                <Td>
                  <StatusBadge
                    label={t(`tasks.priority.${task.priority}`, task.priority)}
                    colorScheme={TASK_PRIORITY_COLORS[task.priority] || "gray"}
                  />
                </Td>
                <Td>{task.due_date || "—"}</Td>
                <Td>
                  {task.owner_first_name
                    ? `${task.owner_first_name} ${task.owner_last_name || ""}`
                    : "—"}
                </Td>
                <Td>
                  {task.assignee_first_name
                    ? `${task.assignee_first_name} ${task.assignee_last_name || ""}`
                    : "—"}
                </Td>
                <Td title={task.entity_label || ""}>
                  {task.entity_type ? (
                    <>
                      <Box
                        as="span"
                        fontSize="11px"
                        fontWeight="800"
                        color="portal.muted"
                      >
                        {t(`tasks.entityType.${task.entity_type}`, task.entity_type)}:{" "}
                      </Box>
                      {task.entity_label || `#${task.entity_id}`}
                    </>
                  ) : (
                    "—"
                  )}
                </Td>
                <Td>
                  <HStack justify="flex-end" spacing="6px">
                    <MiniIconButton
                      data-testid={`task-edit-${task.id}`}
                      icon={<EditIcon />}
                      aria-label={t("tasks.edit", "Edit")}
                      onClick={() => handleOpenEdit(task)}
                    />
                    <MiniIconButton
                      data-testid={`task-delete-${task.id}`}
                      icon={<DeleteIcon />}
                      aria-label={t("tasks.delete", "Delete")}
                      color="portal.danger"
                      _hover={{
                        bg: "danger.50",
                        borderColor: "portal.danger",
                        color: "portal.danger",
                      }}
                      onClick={() => {
                        setDeletingId(task.id);
                        onDeleteOpen();
                      }}
                    />
                  </HStack>
                </Td>
              </Tr>
            )}
          />
        </Panel>
      </Box>

      {/* Create/Edit Modal */}
      <Modal isOpen={isOpen} onClose={onClose} size="xl">
        <ModalOverlay bg="rgba(11,31,58,.45)" />
        <ModalContent sx={MODAL_CONTENT_SX}>
          <ModalHeader sx={MODAL_HEADER_SX}>
            {editingId
              ? t("tasks.editTask", "Edit task")
              : t("tasks.createTask", "New task")}
          </ModalHeader>
          <ModalCloseButton />
          <ModalBody>
            <FormControl mb={3} isRequired>
              <FormLabel>{t("tasks.titleLabel", "Title")}</FormLabel>
              <Input
                data-testid="task-title-input"
                value={form.title}
                onChange={(e) => setForm({ ...form, title: e.target.value })}
                maxLength={255}
              />
            </FormControl>
            <FormControl mb={3}>
              <FormLabel>{t("tasks.descriptionLabel", "Description")}</FormLabel>
              <Textarea
                value={form.description}
                onChange={(e) =>
                  setForm({ ...form, description: e.target.value })
                }
                rows={3}
              />
            </FormControl>
            <SimpleGrid columns={2} spacing={4} mb={3}>
              <FormControl>
                <FormLabel>{t("tasks.dueDateLabel", "Due date")}</FormLabel>
                <Input
                  type="date"
                  value={form.due_date}
                  onChange={(e) =>
                    setForm({ ...form, due_date: e.target.value })
                  }
                />
              </FormControl>
              <FormControl>
                <FormLabel>{t("tasks.priorityLabel", "Priority")}</FormLabel>
                <Select
                  value={form.priority}
                  onChange={(e) =>
                    setForm({ ...form, priority: e.target.value })
                  }
                >
                  {PRIORITY_OPTIONS.map((p) => (
                    <option key={p} value={p}>
                      {t(`tasks.priority.${p}`, p)}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </SimpleGrid>
            <SimpleGrid columns={2} spacing={4} mb={3}>
              <FormControl>
                <FormLabel>{t("tasks.statusLabel", "Status")}</FormLabel>
                <Select
                  value={form.status}
                  onChange={(e) => setForm({ ...form, status: e.target.value })}
                >
                  {STATUS_OPTIONS.map((s) => (
                    <option key={s} value={s}>
                      {t(`tasks.status.${s}`, s)}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl>
                <FormLabel>{t("tasks.assigneeLabel", "Assign to")}</FormLabel>
                <Select
                  value={form.assigned_to_user_id}
                  onChange={(e) =>
                    setForm({ ...form, assigned_to_user_id: e.target.value })
                  }
                  placeholder={t("tasks.assigneePlaceholder", "Unassigned")}
                >
                  {buUsers.map((u) => {
                    const name =
                      `${u.first_name || ""} ${u.last_name || ""}`.trim() ||
                      u.email;
                    return (
                      <option key={u.id} value={u.id}>
                        {name}
                      </option>
                    );
                  })}
                </Select>
              </FormControl>
            </SimpleGrid>
            <SimpleGrid columns={2} spacing={4}>
              <FormControl>
                <FormLabel>
                  {t("tasks.entityTypeLabel", "Link to entity type")}
                </FormLabel>
                <Select
                  value={form.entity_type}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      entity_type: e.target.value,
                      entity_id: "",
                    })
                  }
                  placeholder={t("tasks.entityTypePlaceholder", "None")}
                >
                  {ENTITY_TYPE_OPTIONS.map((et) => (
                    <option key={et} value={et}>
                      {t(`tasks.entityType.${et}`, et)}
                    </option>
                  ))}
                </Select>
              </FormControl>
              <FormControl isDisabled={!form.entity_type}>
                <FormLabel>{t("tasks.entityIdLabel", "Linked entity")}</FormLabel>
                <Select
                  value={form.entity_id}
                  onChange={(e) =>
                    setForm({ ...form, entity_id: e.target.value })
                  }
                  placeholder={
                    entityLoading
                      ? t("tasks.entityLoading", "Loading...")
                      : form.entity_type
                      ? t("tasks.entityPlaceholder", "Select an entity")
                      : t("tasks.entityTypeFirst", "Select a type first")
                  }
                  isDisabled={!form.entity_type || entityLoading}
                >
                  {entityOptions.map((opt) => (
                    <option key={opt.id} value={opt.id}>
                      {opt.label}
                    </option>
                  ))}
                </Select>
              </FormControl>
            </SimpleGrid>
          </ModalBody>
          <ModalFooter sx={MODAL_FOOTER_SX}>
            <Button variant="secondary" size="sm" mr={3} onClick={onClose}>
              {t("common.cancel", "Cancel")}
            </Button>
            <Button
              data-testid="task-save-btn"
              variant="primary"
              size="sm"
              onClick={handleSave}
              isLoading={saving}
            >
              {editingId
                ? t("common.save", "Save")
                : t("common.create", "Create")}
            </Button>
          </ModalFooter>
        </ModalContent>
      </Modal>

      {/* Delete confirmation */}
      <ConfirmDialog
        isOpen={isDeleteOpen}
        onClose={onDeleteClose}
        onConfirm={handleDelete}
        title={t("tasks.deleteConfirmTitle", "Delete task")}
        body={t(
          "tasks.deleteConfirmBody",
          "Are you sure you want to delete this task? This action cannot be undone."
        )}
        confirmLabel={t("common.delete", "Delete")}
        colorScheme="red"
      />
    </Box>
  );
}

// Empty-state node for the tasks table. Kept as a tiny component so the
// useTranslation-bound `t` reads cleanly inside <DataTable emptyState=...>.
function EmptyTasks({ t }) {
  return (
    <EmptyState
      icon={<FaTasks />}
      title={t("tasks.empty", "No tasks found.")}
      hint={t(
        "tasks.emptyHint",
        "Create a task to start tracking your follow-ups."
      )}
    />
  );
}
